import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import QuickMenu from '../../components/QuickMenu/QuickMenu';
import { getGarbageMapData, getGarbageNeighborhoods } from '../../api/garbageServices';
import { toBlob } from 'html-to-image';
import './GarbageServices.scss';

const LEAFLET_CSS_ID = 'leaflet-css';
const LEAFLET_SCRIPT_ID = 'leaflet-script';
const LEAFLET_HEAT_SCRIPT_ID = 'leaflet-heat-script';

const todayAsInputValue = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = `${today.getMonth() + 1}`.padStart(2, '0');
  const day = `${today.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ensureLeafletAssets = () => new Promise((resolve, reject) => {
  const loadHeatLayer = () => {
    if (window.L && window.L.heatLayer) {
      resolve(window.L);
      return;
    }
    const heatScript = document.createElement('script');
    heatScript.id = LEAFLET_HEAT_SCRIPT_ID;
    heatScript.src = 'https://cdn.jsdelivr.net/npm/leaflet.heat@0.2.0/dist/leaflet-heat.js';
    heatScript.async = true;
    heatScript.onload = () => resolve(window.L);
    heatScript.onerror = reject;
    document.body.appendChild(heatScript);
  };

  if (window.L && window.L.heatLayer) {
    resolve(window.L);
    return;
  }

  if (!document.getElementById(LEAFLET_CSS_ID)) {
    const link = document.createElement('link');
    link.id = LEAFLET_CSS_ID;
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    link.crossOrigin = '';
    document.head.appendChild(link);
  }

  const existingScript = document.getElementById(LEAFLET_SCRIPT_ID);
  if (existingScript) {
    if (window.L) {
      loadHeatLayer();
    } else {
      existingScript.addEventListener('load', loadHeatLayer);
      existingScript.addEventListener('error', reject);
    }
    return;
  }

  const script = document.createElement('script');
  script.id = LEAFLET_SCRIPT_ID;
  script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
  script.async = true;
  script.onload = loadHeatLayer;
  script.onerror = reject;
  document.body.appendChild(script);
});

const createBinIcon = (L, isCleaned) => L.icon({
  iconUrl: isCleaned ? '/assets/images/green_bin.png' : '/assets/images/red_bin.png',
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32]
});

export default function GarbageServices() {
  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);
  const leafletLayersRef = useRef([]);
  const fileInputRef = useRef(null);
  const neighborhoodStreetsCacheRef = useRef({});

  const [selectedDate] = useState(todayAsInputValue());
  const [selectedNeighborhood, setSelectedNeighborhood] = useState('');
  const [selectedUserType, setSelectedUserType] = useState(3); // 3: Container, 4: Sweeper
  const [detailData, setDetailData] = useState(null);
  const [neighborhoodOptions, setNeighborhoodOptions] = useState([]);
  const [isPreviewLoading, setIsPreviewLoading] = useState(true);
  const [isNeighborhoodListLoading, setIsNeighborhoodListLoading] = useState(false);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const activeData = detailData;

  const [reportLocations, setReportLocations] = useState([]);
  const [cleaningDepartment, setCleaningDepartment] = useState(null);
  const [complaintModalData, setComplaintModalData] = useState(null);
  const [complaintForm, setComplaintForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    description: '',
    files: [],
    consentAccepted: false
  });
  const [complaintErrors, setComplaintErrors] = useState({});
  const [isComplaintSubmitting, setIsComplaintSubmitting] = useState(false);
  const [isComplaintSuccess, setIsComplaintSuccess] = useState(false);


  useEffect(() => {
    const loadIncidentOptions = async () => {
      try {
        const rawApiUrl = process.env.REACT_APP_API_URL || `${window.location.origin}/api`;
        const API_URL = rawApiUrl.endsWith('/api') ? rawApiUrl : `${rawApiUrl.replace(/\/$/, '')}/api`;

        const [depRes, locRes] = await Promise.all([
          fetch(`${API_URL}/ReportIncident/departments`).then(r => r.json()),
          fetch(`${API_URL}/ReportIncident/locations`).then(r => r.json())
        ]);

        if (depRes?.isSuccess && Array.isArray(depRes.data)) {
          const dep = depRes.data.find(d =>
            d.name.toLocaleUpperCase('tr-TR').includes('TEMİZLİK')
          );
          setCleaningDepartment(dep || null);
        }

        if (locRes?.isSuccess && Array.isArray(locRes.data)) {
          setReportLocations(locRes.data);
        }
      } catch (error) {
        console.error('Şikayet seçenekleri yüklenirken hata oluştu:', error);
      }
    };

    loadIncidentOptions();
  }, []);

  const validateComplaintForm = () => {
    const errors = {};
    if (!complaintForm.firstName.trim()) errors.firstName = 'Ad alanı zorunludur.';
    if (!complaintForm.lastName.trim()) errors.lastName = 'Soyad alanı zorunludur.';
    if (!complaintForm.phone.trim()) {
      errors.phone = 'Telefon alanı zorunludur.';
    } else if (!/^0\d{10}$/.test(complaintForm.phone)) {
      errors.phone = 'Telefon numarası 0 ile başlayan 11 rakam olmalıdır.';
    }
    if (!complaintForm.description.trim()) {
      errors.description = 'Şikayet açıklaması zorunludur.';
    } else if (complaintForm.description.trim().length < 10) {
      errors.description = 'Açıklama en az 10 karakter olmalıdır.';
    }
    if (!complaintForm.consentAccepted) {
      errors.consentAccepted = 'Onay gereklidir.';
    }
    setComplaintErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleComplaintSubmit = async (e) => {
    e.preventDefault();
    if (!validateComplaintForm() || isComplaintSubmitting) return;

    const matchedLoc = reportLocations.find(l =>
      l.name.toLocaleUpperCase('tr-TR').replace(/\s+MAHALLESİ$/, '').trim() ===
      complaintModalData.neighborhood.toLocaleUpperCase('tr-TR').replace(/\s+MAHALLESİ$/, '').trim()
    ) || reportLocations[0];

    if (!matchedLoc) {
      alert('Şikayet kaydı oluşturulamadı: İlgili mahalle sistemde tanımlı değil.');
      return;
    }

    if (!cleaningDepartment) {
      alert('Şikayet kaydı oluşturulamadı: Temizlik İşleri Müdürlüğü birimi sistemde tanımlı değil.');
      return;
    }

    const payload = new FormData();
    payload.append('FirstName', complaintForm.firstName.trim());
    payload.append('LastName', complaintForm.lastName.trim());
    payload.append('Phone', complaintForm.phone.trim());
    payload.append('NeighborhoodGuid', matchedLoc.guid);
    payload.append('NeighborhoodName', matchedLoc.name);

    let addressDetailText = '';
    if (complaintModalData.isSweeper) {
      const googleMapsUrl = `https://www.google.com/maps?q=${complaintModalData.latitude.toFixed(6)},${complaintModalData.longitude.toFixed(6)}`;
      addressDetailText = `[Süpürge Şikayeti] Konum: ${complaintModalData.latitude.toFixed(6)}, ${complaintModalData.longitude.toFixed(6)}, Durum: ${complaintModalData.statusText}, Harita: ${googleMapsUrl}`;
    } else {
      const binLabel = complaintModalData.binName || complaintModalData.binId || 'Konteyner';
      addressDetailText = `[Konteyner Şikayeti] No: ${binLabel}, Adres: ${complaintModalData.binAddress || ''}`;
    }
    payload.append('AddressDetail', addressDetailText.trim());
    payload.append('EventLocation', `${matchedLoc.name} ${addressDetailText}`.trim());

    payload.append('EventDescription', complaintForm.description.trim());
    payload.append('DepartmentGuid', cleaningDepartment.guid);
    payload.append('DepartmentName', cleaningDepartment.name);
    payload.append('ConsentAccepted', String(complaintForm.consentAccepted));

    complaintForm.files.forEach((file) => payload.append('files', file));

    setIsComplaintSubmitting(true);
    try {
      const rawApiUrl = process.env.REACT_APP_API_URL || `${window.location.origin}/api`;
      const API_URL = rawApiUrl.endsWith('/api') ? rawApiUrl : `${rawApiUrl.replace(/\/$/, '')}/api`;

      const response = await fetch(`${API_URL}/ReportIncident`, {
        method: 'POST',
        body: payload
      });

      const resText = await response.text();
      let resJson;
      try {
        resJson = JSON.parse(resText);
      } catch {
        throw new Error('API yanıtı çözümlenemedi.');
      }

      if (!response.ok || !resJson?.isSuccess) {
        throw new Error(resJson?.resultMessage || 'Şikayet gönderilemedi.');
      }

      setIsComplaintSuccess(true);
      setComplaintForm({
        firstName: '',
        lastName: '',
        phone: '',
        description: '',
        files: [],
        consentAccepted: false
      });
    } catch (error) {
      alert(`Şikayet iletilirken hata oluştu: ${error.message}`);
    } finally {
      setIsComplaintSubmitting(false);
    }
  };

  const selectedNeighborhoodOption = useMemo(
    () => neighborhoodOptions.find((item) => item.name === selectedNeighborhood) || null,
    [neighborhoodOptions, selectedNeighborhood]
  );

  const fetchNeighborhoodOptions = useCallback(async () => {
    setIsNeighborhoodListLoading(true);
    setIsPreviewLoading(true);
    setErrorMessage('');

    try {
      const data = await getGarbageNeighborhoods({ date: selectedDate, selectedUserType });
      setNeighborhoodOptions(data?.neighborhoods || []);
    } catch (error) {
      setNeighborhoodOptions([]);
      setIsPreviewLoading(false);
      setErrorMessage(
        error?.response?.data?.message ||
        'Mahalle listesi alınamadı.'
      );
    } finally {
      setIsNeighborhoodListLoading(false);
    }
  }, [selectedDate, selectedUserType]);

  const fetchNeighborhoodDetail = useCallback(async (neighborhoodName) => {
    if (!neighborhoodName) {
      setSelectedNeighborhood('');
      setDetailData(null);
      return;
    }

    const option = neighborhoodOptions.find((item) => item.name === neighborhoodName);
    if (!option) {
      return;
    }

    setIsDetailLoading(true);
    setSelectedNeighborhood(neighborhoodName);
    setErrorMessage('');

    try {
      const data = await getGarbageMapData({
        zoneId: option.zoneId,
        date: selectedDate,
        selectedUserType,
        neighborhoodName
      });

      setDetailData(data);
      setNeighborhoodOptions((currentOptions) => currentOptions.map((item) => (
        item.name === neighborhoodName
          ? {
            ...item,
            totalBins: data?.totalBins ?? 0,
            cleanedBins: data?.cleanedBins ?? 0,
            pendingBins: data?.pendingBins ?? 0
          }
          : item
      )));
    } catch (error) {
      setDetailData(null);
      setErrorMessage(
        error?.response?.data?.message ||
        'Mahalle detay verisi alınamadı.'
      );
    } finally {
      setIsDetailLoading(false);
    }
  }, [neighborhoodOptions, selectedDate, selectedUserType]);

  useEffect(() => {
    setSelectedNeighborhood('');
    setDetailData(null);
  }, [selectedUserType]);

  useEffect(() => {
    fetchNeighborhoodOptions();
  }, [fetchNeighborhoodOptions]);

  useEffect(() => {
    if (!neighborhoodOptions.length || selectedNeighborhood || isDetailLoading) {
      return;
    }

    const defaultNeighborhood = neighborhoodOptions.find((item) => {
      const normalizedName = item.name
        .toLocaleUpperCase('tr-TR')
        .replace(/\s+MAHALLESİ$/, '')
        .trim();

      return normalizedName === 'ÇAY';
    });

    if (defaultNeighborhood) {
      fetchNeighborhoodDetail(defaultNeighborhood.name)
        .finally(() => setIsPreviewLoading(false));
    } else {
      setIsPreviewLoading(false);
    }
  }, [fetchNeighborhoodDetail, isDetailLoading, neighborhoodOptions, selectedNeighborhood]);

  useEffect(() => {
    let isMounted = true;

    const initializeMap = async () => {
      if (!mapRef.current || !activeData) {
        return;
      }

      const L = await ensureLeafletAssets();
      if (!isMounted || !mapRef.current) {
        return;
      }

      if (!leafletMapRef.current) {
        leafletMapRef.current = L.map(mapRef.current, {
          zoomControl: true,
          minZoom: 11
        });

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; OpenStreetMap katkıcıları',
          crossOrigin: true
        }).addTo(leafletMapRef.current);

        leafletMapRef.current.on('popupopen', (e) => {
          const container = e.popup.getElement();
          if (!container) return;
          const btn = container.querySelector('.garbage-report-btn');
          if (btn) {
            btn.onclick = (event) => {
              event.preventDefault();
              const binId = btn.getAttribute('data-bin-id');
              const binName = btn.getAttribute('data-bin-name');
              const binAddress = btn.getAttribute('data-bin-address');
              const neighborhood = btn.getAttribute('data-neighborhood');

              setComplaintModalData({ binId, binName, binAddress, neighborhood });
              leafletMapRef.current.closePopup();
            };
          }

          const sweeperBtn = container.querySelector('.sweeper-report-btn');
          if (sweeperBtn) {
            sweeperBtn.onclick = async (event) => {
              event.preventDefault();
              const lat = parseFloat(sweeperBtn.getAttribute('data-lat'));
              const lon = parseFloat(sweeperBtn.getAttribute('data-lon'));
              const statusText = sweeperBtn.getAttribute('data-status');
              const neighborhood = sweeperBtn.getAttribute('data-neighborhood');

              leafletMapRef.current.closePopup();

              setComplaintModalData({
                isSweeper: true,
                latitude: lat,
                longitude: lon,
                statusText,
                neighborhood,
                screenshotFile: null,
                isScreenshotLoading: true
              });

              setComplaintForm((prev) => ({
                ...prev,
                files: []
              }));

              // Run screenshot capture asynchronously in the background so the form modal opens instantly
              (async () => {
                try {
                  // 1. Temporarily inline CSS styles (blur and opacity) onto the SVG path elements.
                  // Serialized SVG data URIs used by html-to-image ignore external/scoped page stylesheets.
                  const svgPane = mapRef.current.querySelector('.leaflet-overlay-pane svg');
                  const originalPathStyles = [];

                  if (svgPane) {
                    const paths = Array.from(svgPane.querySelectorAll('path'));
                    paths.forEach((p) => {
                      const isRed = p.classList.contains('glowing-street-red');
                      const isGreen = p.classList.contains('glowing-street-green');
                      const isYellow = p.classList.contains('glowing-street-yellow');

                      if (isRed || isGreen || isYellow) {
                        originalPathStyles.push({
                          element: p,
                          filter: p.style.filter,
                          opacity: p.style.opacity,
                          strokeOpacity: p.style.strokeOpacity
                        });

                        // Force styling inline so it gets serialized correctly
                        p.style.filter = 'blur(5px)';
                        p.style.strokeOpacity = '1';
                        p.style.opacity = isRed ? '0.85' : '0.95';
                      }
                    });
                  }

                  // 2. Instantly hide any active leaflet popups so they don't appear in the screenshot (even mid-fadeout)
                  const popups = Array.from(mapRef.current.querySelectorAll('.leaflet-popup'));
                  const originalPopupDisplays = popups.map((popup) => {
                    const originalDisplay = popup.style.display;
                    popup.style.display = 'none';
                    return { popup, originalDisplay };
                  });

                  // Small delay to allow DOM style updates to settle
                  await new Promise((resolve) => setTimeout(resolve, 50));

                  const blob = await toBlob(mapRef.current, {
                    cacheBust: true,
                  });

                  // Restore path styles
                  originalPathStyles.forEach((item) => {
                    item.element.style.filter = item.filter;
                    item.element.style.opacity = item.opacity;
                    item.element.style.strokeOpacity = item.strokeOpacity;
                  });

                  // Restore popups
                  originalPopupDisplays.forEach((item) => {
                    item.popup.style.display = item.originalDisplay;
                  });

                  const screenshotFile = new File(
                    [blob],
                    `harita_konum_${lat.toFixed(6)}_${lon.toFixed(6)}.png`,
                    { type: 'image/png' }
                  );

                  // Update modal data to attach screenshot and mark loading as finished
                  setComplaintModalData((prev) => {
                    if (!prev) return null; // user might have closed the modal already
                    return {
                      ...prev,
                      screenshotFile,
                      isScreenshotLoading: false
                    };
                  });

                  setComplaintForm((prev) => ({
                    ...prev,
                    files: [screenshotFile]
                  }));
                } catch (err) {
                  console.error('Failed to capture map screenshot in background:', err);
                  setComplaintModalData((prev) => {
                    if (!prev) return null;
                    return {
                      ...prev,
                      isScreenshotLoading: false
                    };
                  });
                }
              })();
            };
          }
        });
      }

      leafletLayersRef.current.forEach((layer) => layer.remove());
      leafletLayersRef.current = [];

      const bounds = [];
      const cleanedIcon = createBinIcon(L, true);
      const pendingIcon = createBinIcon(L, false);

      // Draw neighborhood boundaries if coordinates are available
      if (activeData.boundaryCoordinates && activeData.boundaryCoordinates.length > 0) {
        const polygon = L.polygon(activeData.boundaryCoordinates, {
          color: 'rgb(8, 73, 119)',
          fill: false,
          weight: 2,
          dashArray: '5, 5'
        }).addTo(leafletMapRef.current);

        leafletLayersRef.current.push(polygon);
        activeData.boundaryCoordinates.forEach((coord) => {
          bounds.push(coord);
        });
      }

      if (selectedUserType === 4) {
        const getDistanceMeters = (lat1, lon1, lat2, lon2) => {
          const dLat = (lat1 - lat2) * 111000;
          const dLon = (lon2 - lon1) * 89000;
          return Math.sqrt(dLat * dLat + dLon * dLon);
        };

        const sweeperPoints = [];
        if (activeData.sweeperPaths && activeData.sweeperPaths.length > 0) {
          activeData.sweeperPaths.forEach((path) => {
            if (!path.coordinates) return;
            path.coordinates.forEach((coord) => {
              sweeperPoints.push({
                lat: coord[0],
                lon: coord[1],
                color: path.color
              });
            });
          });
        }

        if (selectedNeighborhood) {
          const slug = selectedNeighborhood
            .toLocaleUpperCase('tr-TR')
            .replace(/\s+MAHALLESİ$/, '')
            .replace(/\s+MAH\.$/, '')
            .trim()
            .toLowerCase()
            .replace(/ç/g, 'c')
            .replace(/ğ/g, 'g')
            .replace(/ı/g, 'i')
            .replace(/ö/g, 'o')
            .replace(/ş/g, 's')
            .replace(/ü/g, 'u')
            .replace(/\s+/g, '_');

          if (!neighborhoodStreetsCacheRef.current[slug]) {
            try {
              const res = await fetch(`/data/streets/${slug}.json`);
              if (res.ok) {
                neighborhoodStreetsCacheRef.current[slug] = await res.json();
              } else {
                neighborhoodStreetsCacheRef.current[slug] = [];
              }
            } catch (err) {
              console.error(`Failed to load streets for ${selectedNeighborhood}:`, err);
              neighborhoodStreetsCacheRef.current[slug] = [];
            }
          }

          const streets = neighborhoodStreetsCacheRef.current[slug] || [];
          const redLatlngs = [];
          const greenLatlngs = [];
          const yellowLatlngs = [];

          const flatStreetCoords = [];
          streets.forEach((street) => {
            if (street.geometry) {
              street.geometry.forEach((pt) => {
                flatStreetCoords.push(pt);
              });
            }
          });

          streets.forEach((street) => {
            if (!street.geometry || street.geometry.length < 2) return;
            const latlngs = street.geometry.map((pt) => [pt.lat, pt.lon]);

            let streetColor = '#ef4444'; // default red
            let hasMatchedGreen = false;
            let hasMatchedYellow = false;

            for (let i = 0; i < street.geometry.length; i++) {
              const sPt = street.geometry[i];
              for (let j = 0; j < sweeperPoints.length; j++) {
                const swPt = sweeperPoints[j];
                const dist = getDistanceMeters(sPt.lat, sPt.lon, swPt.lat, swPt.lon);
                if (dist < 35) { // 35 meters threshold
                  if (swPt.color === 'green') {
                    hasMatchedGreen = true;
                    break;
                  } else if (swPt.color === 'yellow') {
                    hasMatchedYellow = true;
                  }
                }
              }
              if (hasMatchedGreen) break;
            }

            if (hasMatchedGreen) {
              streetColor = '#22c55e'; // green
              greenLatlngs.push(latlngs);
            } else if (hasMatchedYellow) {
              streetColor = '#eab308'; // yellow
              yellowLatlngs.push(latlngs);
            } else {
              streetColor = '#ef4444'; // red
              redLatlngs.push(latlngs);
            }

            // Draw interaction layer (invisible but clickable)
            const polyline = L.polyline(latlngs, {
              color: 'transparent',
              weight: 16,
              opacity: 0,
              className: 'street-interaction-layer',
              interactive: true
            }).addTo(leafletMapRef.current);

            leafletLayersRef.current.push(polyline);

            polyline.on('click', (e) => {
              const clickedLatLng = e.latlng;
              const cleanStatusText = streetColor === '#22c55e'
                ? 'Son 2 Saat İçinde Süpürüldü'
                : streetColor === '#eab308'
                  ? '2 - 6 Saat Önce Süpürüldü'
                  : 'Süpürülmedi (6+ Saat)';

              const popupContent = `
                <div style="font-family:inherit;font-size:0.875rem;min-width:180px;">
                  <span>Durum: <span style="font-weight:bold;color:${streetColor};">${cleanStatusText}</span></span>
                  <div style="margin-top:10px;border-top:1px solid #e2e8f0;padding-top:8px;">
                    <button type="button" class="sweeper-report-btn" 
                      data-lat="${clickedLatLng.lat}" 
                      data-lon="${clickedLatLng.lng}"
                      data-status="${cleanStatusText}"
                      data-neighborhood="${selectedNeighborhood}"
                      style="width:100%;height:32px;background:#dc2626;color:#fff;border:none;border-radius:6px;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:0.85rem;font-family:inherit;box-shadow:0 2px 4px rgba(220,38,38,0.2);">
                      Şikayet Bildir
                    </button>
                  </div>
                </div>
              `;

              L.popup()
                .setLatLng(clickedLatLng)
                .setContent(popupContent)
                .openOn(leafletMapRef.current);
            });
          });

          // Extract off-road segments from sweeper paths and render them as green/yellow glowing paths
          if (activeData.sweeperPaths && activeData.sweeperPaths.length > 0) {
            activeData.sweeperPaths.forEach((path) => {
              if (!path.coordinates || path.coordinates.length === 0) return;

              let currentOffRoadSegment = [];

              path.coordinates.forEach((coord) => {
                const lat = coord[0];
                const lon = coord[1];
                const close = flatStreetCoords.some(
                  (pt) => getDistanceMeters(lat, lon, pt.lat, pt.lon) < 35
                );

                if (!close) {
                  currentOffRoadSegment.push([lat, lon]);
                } else {
                  if (currentOffRoadSegment.length >= 2) {
                    if (path.color === 'green') {
                      greenLatlngs.push(currentOffRoadSegment);
                    } else if (path.color === 'yellow') {
                      yellowLatlngs.push(currentOffRoadSegment);
                    }

                    const polyline = L.polyline(currentOffRoadSegment, {
                      color: 'transparent',
                      weight: 16,
                      opacity: 0,
                      className: 'street-interaction-layer',
                      interactive: true
                    }).addTo(leafletMapRef.current);

                    leafletLayersRef.current.push(polyline);

                    polyline.on('click', (e) => {
                      const clickedLatLng = e.latlng;
                      const cleanStatusText = path.color === 'green'
                        ? 'Son 2 Saat İçinde Süpürüldü'
                        : '2 - 6 Saat Önce Süpürüldü';
                      const statusColor = path.color === 'green' ? '#22c55e' : '#eab308';

                      const popupContent = `
                        <div style="font-family:inherit;font-size:0.875rem;min-width:180px;">
                          <span>Durum: <span style="font-weight:bold;color:${statusColor};">${cleanStatusText}</span></span>
                          <div style="margin-top:10px;border-top:1px solid #e2e8f0;padding-top:8px;">
                            <button type="button" class="sweeper-report-btn" 
                              data-lat="${clickedLatLng.lat}" 
                              data-lon="${clickedLatLng.lng}"
                              data-status="${cleanStatusText}"
                              data-neighborhood="${selectedNeighborhood}"
                              style="width:100%;height:32px;background:#dc2626;color:#fff;border:none;border-radius:6px;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:0.85rem;font-family:inherit;box-shadow:0 2px 4px rgba(220,38,38,0.2);">
                              Şikayet Bildir
                            </button>
                          </div>
                        </div>
                      `;

                      L.popup()
                        .setLatLng(clickedLatLng)
                        .setContent(popupContent)
                        .openOn(leafletMapRef.current);
                    });
                  }
                  currentOffRoadSegment = [];
                }
              });

              if (currentOffRoadSegment.length >= 2) {
                if (path.color === 'green') {
                  greenLatlngs.push(currentOffRoadSegment);
                } else if (path.color === 'yellow') {
                  yellowLatlngs.push(currentOffRoadSegment);
                }

                const polyline = L.polyline(currentOffRoadSegment, {
                  color: 'transparent',
                  weight: 16,
                  opacity: 0,
                  className: 'street-interaction-layer',
                  interactive: true
                }).addTo(leafletMapRef.current);

                leafletLayersRef.current.push(polyline);

                polyline.on('click', (e) => {
                  const clickedLatLng = e.latlng;
                  const cleanStatusText = path.color === 'green'
                    ? 'Son 2 Saat İçinde Süpürüldü'
                    : '2 - 6 Saat Önce Süpürüldü';
                  const statusColor = path.color === 'green' ? '#22c55e' : '#eab308';

                  const popupContent = `
                    <div style="font-family:inherit;font-size:0.875rem;min-width:180px;">
                      <span>Durum: <span style="font-weight:bold;color:${statusColor};">${cleanStatusText}</span></span>
                      <div style="margin-top:10px;border-top:1px solid #e2e8f0;padding-top:8px;">
                        <button type="button" class="sweeper-report-btn" 
                          data-lat="${clickedLatLng.lat}" 
                          data-lon="${clickedLatLng.lng}"
                          data-status="${cleanStatusText}"
                          data-neighborhood="${selectedNeighborhood}"
                          style="width:100%;height:32px;background:#dc2626;color:#fff;border:none;border-radius:6px;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:0.85rem;font-family:inherit;box-shadow:0 2px 4px rgba(220,38,38,0.2);">
                          Şikayet Bildir
                        </button>
                      </div>
                    </div>
                  `;

                  L.popup()
                    .setLatLng(clickedLatLng)
                    .setContent(popupContent)
                    .openOn(leafletMapRef.current);
                });
              }
            });
          }

          // Draw the glowing background layers
          if (redLatlngs.length > 0) {
            const redGlow = L.polyline(redLatlngs, {
              color: '#ef4444',
              weight: 18,
              opacity: 0.75,
              className: 'glowing-street-red',
              lineCap: 'round',
              lineJoin: 'round',
              interactive: false
            }).addTo(leafletMapRef.current);
            leafletLayersRef.current.push(redGlow);
          }

          if (greenLatlngs.length > 0) {
            const greenGlow = L.polyline(greenLatlngs, {
              color: '#22c55e',
              weight: 18,
              opacity: 0.85,
              className: 'glowing-street-green',
              lineCap: 'round',
              lineJoin: 'round',
              interactive: false
            }).addTo(leafletMapRef.current);
            leafletLayersRef.current.push(greenGlow);
          }

          if (yellowLatlngs.length > 0) {
            const yellowGlow = L.polyline(yellowLatlngs, {
              color: '#eab308',
              weight: 18,
              opacity: 0.85,
              className: 'glowing-street-yellow',
              lineCap: 'round',
              lineJoin: 'round',
              interactive: false
            }).addTo(leafletMapRef.current);
            leafletLayersRef.current.push(yellowGlow);
          }
        }
      } else {
        // Draw container bins
        (activeData.bins || []).forEach((bin) => {
          if (typeof bin.latitude !== 'number' || typeof bin.longitude !== 'number') {
            return;
          }

          const marker = L.marker([bin.latitude, bin.longitude], {
            icon: bin.isCleaned ? cleanedIcon : pendingIcon
          })
            .bindPopup(`
              <div style="min-width:220px">
                <strong>Konteyner numarası: ${bin.name || bin.id || 'Konteyner'}</strong><br/>
                <span>Durum: ${bin.statusText}</span>
                ${bin.isCleaned && bin.lastVisitTime ? `<br/><span>Temizlenme saati: ${bin.lastVisitTime}</span>` : ''}
                <div style="margin-top:10px;border-top:1px solid #e2e8f0;padding-top:8px;">
                  <button type="button" class="garbage-report-btn" 
                    data-bin-id="${bin.id || ''}" 
                    data-bin-name="${bin.name || ''}" 
                    data-bin-address="${bin.address || ''}" 
                    data-neighborhood="${bin.neighborhoodName || ''}"
                    style="width:100%;height:32px;background:#dc2626;color:#fff;border:none;border-radius:6px;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:0.85rem;font-family:inherit;box-shadow:0 2px 4px rgba(220,38,38,0.2);">
                    Şikayet Bildir
                  </button>
                </div>
              </div>
            `)
            .addTo(leafletMapRef.current);

          leafletLayersRef.current.push(marker);
          bounds.push([bin.latitude, bin.longitude]);
        });
      }

      if (bounds.length === 1) {
        leafletMapRef.current.setView(bounds[0], 17);
      } else if (bounds.length > 1) {
        leafletMapRef.current.fitBounds(bounds, { padding: [36, 36] });
      } else {
        leafletMapRef.current.setView([36.5872, 36.1734], 13);
      }
    };

    initializeMap().catch(() => {
      if (isMounted) {
        setErrorMessage('Harita bileşeni yüklenemedi.');
      }
    });

    return () => {
      isMounted = false;
    };
  }, [activeData]);

  useEffect(() => () => {
    if (leafletMapRef.current) {
      leafletMapRef.current.remove();
      leafletMapRef.current = null;
    }
  }, []);

  return (
    <div className="garbage-services-page">
      <div className="container-fluid">
        <div className="garbage-services-shell">
          {errorMessage ? (
            <div className="garbage-services-feedback is-error">{errorMessage}</div>
          ) : null}

          <aside className="garbage-services-quick-menu">
            <QuickMenu />
          </aside>

          <div className="garbage-services-content">
            <div className="garbage-services-map-card">
              <div className="garbage-services-map-header" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', alignItems: 'stretch' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                  <div>
                    <h2 className="garbage-services-section-title">
                      {selectedUserType === 4 ? 'Süpürge Haritası' : 'Konteyner Haritası'}
                    </h2>
                    <div className="garbage-services-helper">
                      {selectedNeighborhood
                        ? `${selectedNeighborhood} Mahallesi ${selectedUserType === 4 ? 'süpürge temizlik rotaları' : 'konteyner verileri'} gösteriliyor.`
                        : 'Mahalle verisi hazırlanıyor.'}
                    </div>
                  </div>
                  <div className="garbage-services-mode-tabs" style={{ display: 'flex', background: '#f1f5f9', padding: '4px', borderRadius: '12px' }}>
                    <button
                      type="button"
                      onClick={() => setSelectedUserType(3)}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: 'none',
                        background: selectedUserType === 3 ? '#fff' : 'transparent',
                        color: selectedUserType === 3 ? '#084977' : '#64748b',
                        fontWeight: '600',
                        fontSize: '0.875rem',
                        cursor: 'pointer',
                        boxShadow: selectedUserType === 3 ? '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)' : 'none',
                        transition: 'all 0.2s'
                      }}
                    >
                      Konteyner Haritası
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedUserType(4)}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: 'none',
                        background: selectedUserType === 4 ? '#fff' : 'transparent',
                        color: selectedUserType === 4 ? '#084977' : '#64748b',
                        fontWeight: '600',
                        fontSize: '0.875rem',
                        cursor: 'pointer',
                        boxShadow: selectedUserType === 4 ? '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)' : 'none',
                        transition: 'all 0.2s'
                      }}
                    >
                      Süpürge Haritası
                    </button>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                  <div className="garbage-services-mobile-neighborhood">
                    <label htmlFor="garbage-neighborhood-mobile">Mahalle</label>
                    <select
                      id="garbage-neighborhood-mobile"
                      value={selectedNeighborhood}
                      onChange={(event) => fetchNeighborhoodDetail(event.target.value)}
                      disabled={!neighborhoodOptions.length || isDetailLoading}
                    >
                      <option value="">Mahalle seçiniz</option>
                      {neighborhoodOptions.map((option) => (
                        <option key={`mobile-${option.zoneId}-${option.name}`} value={option.name}>
                          {option.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {selectedUserType === 4 ? (
                    <div className="garbage-services-map-meta">
                      <span className="garbage-services-chip" style={{ background: 'rgba(34, 197, 94, 0.1)', color: '#15803d', border: '1px solid rgba(34, 197, 94, 0.2)', padding: '4px 10px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e' }} />
                        Son 2 Saat
                      </span>
                      <span className="garbage-services-chip" style={{ background: 'rgba(234, 179, 8, 0.1)', color: '#a16207', border: '1px solid rgba(234, 179, 8, 0.2)', padding: '4px 10px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#eab308' }} />
                        2 - 6 Saat
                      </span>
                      <span className="garbage-services-chip" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#b91c1c', border: '1px solid rgba(239, 68, 68, 0.2)', padding: '4px 10px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444' }} />
                        6+ Saat
                      </span>
                    </div>
                  ) : (
                    <div className="garbage-services-map-meta">
                      <span className="garbage-services-chip is-cleaned">
                        <img src="/assets/images/green_bin.png" alt="Temizlendi" className="garbage-chip-icon" />
                        Temizlendi: {selectedNeighborhoodOption?.cleanedBins ?? activeData?.cleanedBins ?? 0}
                      </span>
                      <span className="garbage-services-chip is-pending">
                        <img src="/assets/images/red_bin.png" alt="Temizlenecek" className="garbage-chip-icon" />
                        Temizlenecek: {selectedNeighborhoodOption?.pendingBins ?? activeData?.pendingBins ?? 0}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="garbage-services-map-shell">
                <div ref={mapRef} className="garbage-services-map" />
                {isDetailLoading || isPreviewLoading ? (
                  <div className="garbage-services-map-loading">
                    <div className="garbage-services-map-spinner" />
                    <span>Harita güncelleniyor...</span>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="garbage-services-side">
              <div className="garbage-services-side-card garbage-services-neighborhood-card">
                <div className="garbage-services-side-header">
                  <h3 className="garbage-services-section-title">Mahalle Seçimi</h3>
                  <span className="garbage-services-side-count">{neighborhoodOptions.length} kayıt</span>
                </div>
                {isNeighborhoodListLoading ? (
                  <div className="garbage-services-helper">Mahalle listesi hazırlanıyor...</div>
                ) : null}
                <div className="garbage-services-list">
                  {neighborhoodOptions.length ? neighborhoodOptions.map((item) => (
                    <button
                      key={`${item.zoneId}-${item.name}`}
                      type="button"
                      className={`garbage-services-neighborhood-button${selectedNeighborhood === item.name ? ' is-active' : ''}`}
                      onClick={() => fetchNeighborhoodDetail(item.name)}
                      disabled={isDetailLoading}
                    >
                      <div className="garbage-services-neighborhood-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {selectedUserType === 4 && item.statusColor && (
                          <span style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            display: 'inline-block',
                            flexShrink: 0,
                            background: item.statusColor === 'green' ? '#22c55e' : item.statusColor === 'yellow' ? '#eab308' : '#ef4444'
                          }} />
                        )}
                        {item.name}
                      </div>
                      {selectedUserType !== 4 && (
                        <div className="garbage-services-neighborhood-meta">
                          Toplam {item.totalBins} • Temizlendi {item.cleanedBins} • Temizlenecek {item.pendingBins}
                        </div>
                      )}
                    </button>
                  )) : (
                    <div className="garbage-services-feedback">
                      {isNeighborhoodListLoading ? 'Yükleniyor...' : 'Uygun mahalle kaydı bulunamadı.'}
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>

      {complaintModalData && (
        <div className="garbage-modal-overlay">
          <div className="garbage-modal-container">
            <div className="garbage-modal-header">
              <h3 className="garbage-modal-title">
                {complaintModalData.isSweeper ? 'Temizlik Şikayet Bildirimi' : 'Konteyner Şikayet Bildirimi'}
              </h3>
              <button
                type="button"
                className="garbage-modal-close"
                onClick={() => {
                  setComplaintModalData(null);
                  setIsComplaintSuccess(false);
                  setComplaintErrors({});
                }}
              >
                &times;
              </button>
            </div>

            <div className="garbage-modal-body">
              {isComplaintSuccess ? (
                <div className="garbage-modal-success">
                  Şikayet bildiriminiz başarıyla Temizlik İşleri Müdürlüğü'ne iletilmiştir.
                </div>
              ) : (
                <form onSubmit={handleComplaintSubmit}>
                  <div className="garbage-modal-info">
                    {complaintModalData.isSweeper ? (
                      <>
                        <strong>Durum:</strong> {complaintModalData.statusText}<br />
                        <strong>Konum:</strong>{' '}
                        <a
                          href={`https://www.google.com/maps?q=${complaintModalData.latitude.toFixed(6)},${complaintModalData.longitude.toFixed(6)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#2563eb', textDecoration: 'underline', fontWeight: 600 }}
                        >
                          {complaintModalData.latitude.toFixed(6)}, {complaintModalData.longitude.toFixed(6)}
                        </a>
                        <br />
                        <strong>Mahalle:</strong> {complaintModalData.neighborhood} Mahallesi<br />
                      </>
                    ) : (
                      <>
                        <strong>Konteyner:</strong> {complaintModalData.binName || complaintModalData.binId || 'Konteyner'}<br />
                        <strong>Mahalle:</strong> {complaintModalData.neighborhood} Mahallesi<br />
                        {complaintModalData.binAddress && (
                          <div><strong>Adres:</strong> {complaintModalData.binAddress}</div>
                        )}
                      </>
                    )}
                  </div>

                  <div className="garbage-modal-grid">
                    <div className="garbage-modal-group">
                      <label>Ad *</label>
                      <input
                        type="text"
                        value={complaintForm.firstName}
                        onChange={(e) => setComplaintForm(p => ({ ...p, firstName: e.target.value }))}
                        className={complaintErrors.firstName ? 'error' : ''}
                      />
                      {complaintErrors.firstName && <span className="error-text">{complaintErrors.firstName}</span>}
                    </div>
                    <div className="garbage-modal-group">
                      <label>Soyad *</label>
                      <input
                        type="text"
                        value={complaintForm.lastName}
                        onChange={(e) => setComplaintForm(p => ({ ...p, lastName: e.target.value }))}
                        className={complaintErrors.lastName ? 'error' : ''}
                      />
                      {complaintErrors.lastName && <span className="error-text">{complaintErrors.lastName}</span>}
                    </div>
                  </div>

                  <div className="garbage-modal-group">
                    <label>Telefon *</label>
                    <input
                      type="text"
                      value={complaintForm.phone}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '');
                        const withoutLeading = digits.replace(/^0+/, '');
                        setComplaintForm(p => ({ ...p, phone: `0${withoutLeading}`.slice(0, 11) }));
                      }}
                      onFocus={() => !complaintForm.phone && setComplaintForm(p => ({ ...p, phone: '0' }))}
                      maxLength={11}
                      placeholder="05xxxxxxxxx"
                      className={complaintErrors.phone ? 'error' : ''}
                    />
                    {complaintErrors.phone && <span className="error-text">{complaintErrors.phone}</span>}
                  </div>

                  <div className="garbage-modal-group">
                    <label>Şikayet Açıklaması *</label>
                    <textarea
                      rows={3}
                      value={complaintForm.description}
                      onChange={(e) => setComplaintForm(p => ({ ...p, description: e.target.value }))}
                      placeholder="Lütfen şikayetinizi detaylı şekilde açıklayınız."
                      className={complaintErrors.description ? 'error' : ''}
                    />
                    {complaintErrors.description && <span className="error-text">{complaintErrors.description}</span>}
                  </div>

                  <div className="garbage-modal-group">
                    <label>Fotoğraf / Video (İsteğe Bağlı) <small style={{ color: '#64748b', fontWeight: 'normal' }}>(En fazla 5 dosya, her biri max 25 MB)</small></label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,video/*"
                      multiple
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const filesArr = Array.from(e.target.files || []);
                        const validFiles = filesArr.filter(f => f.size <= 25 * 1024 * 1024);
                        if (filesArr.length !== validFiles.length) {
                          alert('25 MB üzerindeki dosyalar elendi.');
                        }
                        setComplaintForm(p => ({ ...p, files: [...p.files, ...validFiles].slice(0, 5) }));
                        e.target.value = '';
                      }}
                    />
                    <div
                      className="garbage-file-drop-zone"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#084977', marginBottom: '8px' }}>
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                      <span>Dosyaları seçmek için tıklayın</span>
                    </div>

                    {(complaintForm.files.length > 0 || complaintModalData.isScreenshotLoading) && (
                      <div className="garbage-modal-files">
                        {complaintModalData.isScreenshotLoading && (
                          <div className="file-item loading" style={{ opacity: 0.8, display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <svg className="animate-spin" style={{ width: '16px', height: '16px', animation: 'garbage-spin 1s linear infinite' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10" stroke="rgba(0,0,0,0.1)" strokeWidth="4" />
                              <path d="M4 12a8 8 0 0 1 8-8" stroke="#084977" strokeWidth="4" strokeLinecap="round" />
                            </svg>
                            <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Harita ekran görüntüsü hazırlanıyor...</span>
                          </div>
                        )}
                        {complaintForm.files.map((file, idx) => (
                          <div key={idx} className="file-item">
                            <span title={file.name}>{file.name}</span>
                            <small style={{ color: '#64748b', margin: '0 8px' }}>{(file.size / 1024 / 1024).toFixed(1)} MB</small>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setComplaintForm(p => ({ ...p, files: p.files.filter((_, i) => i !== idx) }));
                              }}
                            >
                              Kaldır
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <label className={`consent-box ${complaintErrors.consentAccepted ? 'error' : ''}`}>
                    <input
                      type="checkbox"
                      checked={complaintForm.consentAccepted}
                      onChange={(e) => setComplaintForm(p => ({ ...p, consentAccepted: e.target.checked }))}
                    />
                    <span>
                      Geri dönüş yapılabilmesi için bilgilerimin kullanılmasını kabul ediyorum.
                    </span>
                  </label>

                  <button
                    type="submit"
                    className="garbage-modal-submit"
                    disabled={isComplaintSubmitting || complaintModalData.isScreenshotLoading}
                  >
                    {isComplaintSubmitting
                      ? 'Gönderiliyor...'
                      : complaintModalData.isScreenshotLoading
                        ? 'Ekran Görüntüsü Hazırlanıyor...'
                        : 'Şikayeti İlet'
                    }
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
